import React, { useState, useEffect } from 'react';
import { X, Download, Share, Plus, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocation } from 'react-router-dom';

const InstallPWA = () => {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showManualInstall, setShowManualInstall] = useState(false);
  const location = useLocation();

  // Pages where PWA install prompt should show
  const allowedPaths = ['/login', '/register', '/employee', '/admin', '/manager'];
  
  // Check if current page should show PWA prompt
  const shouldShowOnCurrentPage = () => {
    const currentPath = location.pathname;
    // Don't show on landing page or public marketing pages
    if (currentPath === '/' || currentPath === '/signup' || currentPath === '/about') {
      return false;
    }
    // Show on login, register, and authenticated pages
    return allowedPaths.some(path => currentPath.startsWith(path)) || currentPath === '/login';
  };

  // Check if device is mobile
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768);
  };

  // Check if iOS
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  // Check if Safari
  const isSafari = () => {
    return /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
  };

  // Check if Chrome on Android
  const isAndroidChrome = () => {
    return /Android/.test(navigator.userAgent) && /Chrome/.test(navigator.userAgent);
  };

  useEffect(() => {
    // Check if already installed (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    if (isStandalone) {
      console.log('[PWA] Already installed - standalone mode');
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently (within 24 hours for mobile, 7 days for desktop)
    const dismissedAt = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissedAt) {
      const hoursSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
      const dismissPeriod = isMobileDevice() ? 24 : 168; // 24 hours for mobile, 7 days for desktop
      if (hoursSinceDismissed < dismissPeriod) {
        console.log(`[PWA] Dismissed ${hoursSinceDismissed.toFixed(1)} hours ago, waiting ${dismissPeriod} hours`);
        return;
      }
    }

    // Listen for the beforeinstallprompt event (Chrome, Edge, Android)
    const handleBeforeInstallPrompt = (e) => {
      console.log('[PWA] beforeinstallprompt event fired!');
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt immediately on mobile
      if (isMobileDevice()) {
        setTimeout(() => setShowInstallPrompt(true), 1500);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS Safari - show manual instructions
    if (isIOS() && isSafari() && !window.navigator.standalone) {
      console.log('[PWA] iOS Safari detected - showing manual install prompt');
      setTimeout(() => setShowIOSPrompt(true), 2000);
    }
    
    // For Android Chrome - if no beforeinstallprompt after 3 seconds, show manual prompt
    if (isAndroidChrome()) {
      setTimeout(() => {
        if (!deferredPrompt && !showInstallPrompt) {
          console.log('[PWA] Android Chrome - no beforeinstallprompt, showing manual option');
          setShowManualInstall(true);
        }
      }, 3000);
    }

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('[PWA] App installed!');
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setShowIOSPrompt(false);
      setShowManualInstall(false);
      localStorage.removeItem('pwa-prompt-dismissed');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [deferredPrompt, showInstallPrompt]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      console.log('[PWA] No deferred prompt available');
      return;
    }

    console.log('[PWA] Triggering install prompt');
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`[PWA] User choice: ${outcome}`);
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    setShowIOSPrompt(false);
    setShowManualInstall(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  // Don't render if already installed
  if (isInstalled) return null;

  // Don't render on landing page or other public marketing pages
  if (!shouldShowOnCurrentPage()) return null;

  // Android/Chrome Install Prompt (native)
  if (showInstallPrompt && deferredPrompt) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom-4 duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 max-w-sm mx-auto">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-900 to-indigo-700 flex items-center justify-center shrink-0">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900">Install ClockIn Pro</h3>
              <p className="text-sm text-slate-500 mt-0.5">
                Install for faster access & offline support
              </p>
            </div>
            <button 
              onClick={handleDismiss}
              className="text-slate-400 hover:text-slate-600 p-1"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleDismiss}
            >
              Later
            </Button>
            <Button
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              onClick={handleInstall}
              data-testid="install-pwa-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Install App
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // iOS Safari Install Instructions
  if (showIOSPrompt) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom-4 duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 max-w-sm mx-auto">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-900 to-indigo-700 flex items-center justify-center shrink-0">
              <Plus className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900">Install ClockIn Pro</h3>
              <p className="text-sm text-slate-500 mt-0.5">
                Add to home screen for the best experience
              </p>
            </div>
            <button 
              onClick={handleDismiss}
              className="text-slate-400 hover:text-slate-600 p-1"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* iOS Instructions */}
          <div className="mt-4 p-3 bg-slate-50 rounded-xl">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold shrink-0">1</div>
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <span>Tap the</span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                    <Share className="w-4 h-4" /> Share
                  </span>
                  <span>button below</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold shrink-0">2</div>
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <span>Scroll and tap</span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-200 rounded font-medium">
                    <Plus className="w-3 h-3" /> Add to Home Screen
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold shrink-0">3</div>
                <span className="text-sm text-slate-700">Tap <strong>Add</strong> to confirm</span>
              </div>
            </div>
          </div>
          
          <Button
            variant="outline"
            className="w-full mt-4"
            onClick={handleDismiss}
          >
            Got it
          </Button>
        </div>
      </div>
    );
  }

  // Manual install prompt for Android (if beforeinstallprompt didn't fire)
  if (showManualInstall && isMobileDevice()) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom-4 duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 max-w-sm mx-auto">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-900 to-indigo-700 flex items-center justify-center shrink-0">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900">Install ClockIn Pro</h3>
              <p className="text-sm text-slate-500 mt-0.5">
                Add to your home screen for quick access
              </p>
            </div>
            <button 
              onClick={handleDismiss}
              className="text-slate-400 hover:text-slate-600 p-1"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="mt-4 p-3 bg-slate-50 rounded-xl">
            <p className="text-sm text-slate-600 mb-2">To install:</p>
            <div className="space-y-2 text-sm text-slate-700">
              <p>1. Tap the <strong>⋮</strong> menu (top right)</p>
              <p>2. Select <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong></p>
            </div>
          </div>
          
          <Button
            variant="outline"
            className="w-full mt-4"
            onClick={handleDismiss}
          >
            Got it
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default InstallPWA;
